import React, {useReducer} from 'react';
import {CheckIcon, XMarkIcon} from '@heroicons/react/24/outline';
import type {PlanType} from 'api/generated';
import {Button, ButtonClipboard, Modal} from 'components';
import {usePersistentStore} from 'stores';
import {classNames} from 'utils';

import type {PayDurationType} from './Plans.Toggle.PayDuration';
import usePlanTable from '../../hooks/usePlanTable';

interface IPlansTableMobileProps {
  payDuration: PayDurationType;
  setPayDuration: (payDuration: PayDurationType) => void;
  plans: PlanType[];
  currentPlan: PlanType;
  onSubscribe: (plan: PlanType) => void;
}

const PlansTableMobile = ({plans, currentPlan, onSubscribe}: IPlansTableMobileProps) => {
  const [showModal, toggleModal] = useReducer(s => !s, false);
  const countryCode = usePersistentStore(s => s.countryCode.code);
  const {plansWithMine, features, showFeatureOverride, toggleFeatureOverride, shouldHighlight, getFeatureKey} = usePlanTable({plans, currentPlan});
  return (
    <section className="lg:hidden">
      <div className="mx-auto space-y-16">
        {plansWithMine.map(plan => {
          const featureKey = getFeatureKey(plan);
          return (
            <div key={plan.uid}>
              {/* Headers */}
              <div className="flex flex-col items-center pb-4">
                <h3
                  className={classNames(
                    'text-xl font-semibold',
                    currentPlan.variantOf === plan.variantOf ? 'text-primary' : 'text-gray-900 dark:text-white',
                  )}>
                  {plan.name}
                </h3>
                <p className="mt-1 text-sm/6 text-gray-600 dark:text-darkText">{plan.description}</p>
              </div>
              {/* Prices */}
              <div className="pb-5">
                {(() => {
                  if (plan.billing.custom) {
                    return (
                      <div key={plan.uid} className="flex flex-col items-center text-center">
                        <div className="flex items-center mb-1 text-slate-900 dark:text-white">
                          <span className="text-4xl">Custom</span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="flex flex-col items-center text-center">
                      <div className="flex items-center mb-1 text-slate-900 dark:text-white">
                        {/* <span className="text-4xl">{plan.billing.symbol}</span> */}
                        {/* TODO: implement different currency symbol and price */}
                        <span className="text-4xl">{countryCode !== 'MY' ? 'RM' : '$'}</span>
                        <span className="text-4xl">{plan.billing.priceCents / 100}</span>
                      </div>
                      <span className="text-slate-900 dark:text-white leading-6 lowercase text-sm">
                        {/* {plan.billing.currency} / {plan.billing.interval} */}
                        {/* TODO: implement different currency symbol and price */}
                        {countryCode !== 'MY' ? 'myr' : 'usd'} / {plan.billing.interval}
                      </span>
                    </div>
                  );
                })()}
              </div>
              {/* CTA */}
              <div className="flex flex-col items-center px-5 pb-10">
                <div className="max-w-xs w-full flex flex-col">
                  {(() => {
                    if (currentPlan.uid === plan.uid) {
                      return (
                        <Button
                          key={plan.uid}
                          text={showFeatureOverride ? 'Current' : 'Default'}
                          size="small"
                          rounded
                          onClick={toggleFeatureOverride}
                          variant="secondary"
                          disabled={!currentPlan.featuresOverride}
                        />
                      );
                    }
                    if (plan.billing.custom) {
                      return <Button key={plan.uid} text="Contact Us" size="small" rounded onClick={toggleModal} />;
                    }
                    {
                      const isUpgrade = currentPlan.sequence < plan.sequence;
                      const text = isUpgrade ? 'Upgrade' : 'Downgrade';
                      const variant = isUpgrade ? 'primary' : 'secondary';
                      return (
                        <Button
                          key={plan.uid}
                          text={text}
                          size="small"
                          variant={variant}
                          rounded
                          disabled={!isUpgrade}
                          onClick={() => onSubscribe(plan)}
                        />
                      );
                    }
                  })()}
                </div>
              </div>
              {/* Features Listing */}
              <div>
                <h4 className="text-sm/6 font-semibold text-gray-900 dark:text-white">Features</h4>
                <div className="relative mt-6">
                  {/* Fake card background */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-y-0 right-0 hidden w-1/2 rounded-lg bg-white shadow-xs sm:block dark:bg-gray-800/50 dark:shadow-none"
                  />
                  <div
                    className={classNames(
                      shouldHighlight(plan) ? 'ring-2 ring-primary' : 'ring-1 ring-gray-900/10 dark:ring-white/10',
                      'relative rounded-lg bg-white shadow-xs sm:rounded-none sm:bg-transparent sm:shadow-none dark:bg-gray-800/50 dark:shadow-none dark:sm:bg-transparent',
                    )}>
                    <dl className="divide-y divide-gray-200 text-sm/6 dark:divide-white/10">
                      {features.map(feature => {
                        return (
                          <div key={feature.name} className="flex items-center justify-between px-4 py-3 sm:grid sm:grid-cols-2 sm:px-0">
                            <dt className="pr-4 text-gray-600 dark:text-gray-400">{feature.name}</dt>
                            <dd className="flex items-center justify-end sm:justify-center sm:px-4">
                              {typeof feature.plans[plan.name][featureKey] === 'string' ? (
                                <span className={shouldHighlight(plan) ? 'font-semibold text-primary' : 'text-gray-900 dark:text-white'}>
                                  {feature.plans[plan.name][featureKey]}
                                </span>
                              ) : (
                                <>
                                  <span className="sr-only">{feature.plans[plan.name][featureKey] === true ? 'Yes' : 'No'}</span>
                                  {feature.plans[plan.name][featureKey] === true ? (
                                    <CheckIcon
                                      aria-hidden="true"
                                      className={classNames(
                                        'mx-auto size-5',
                                        shouldHighlight(plan) ? 'text-primary' : 'text-gray-800 dark:text-white',
                                      )}
                                    />
                                  ) : (
                                    <XMarkIcon aria-hidden="true" className="mx-auto size-5 text-gray-400 dark:text-darkText/50" />
                                  )}
                                </>
                              )}
                            </dd>
                          </div>
                        );
                      })}
                    </dl>
                  </div>
                  {/* Fake card border */}
                  <div
                    aria-hidden="true"
                    className={classNames(
                      currentPlan.variantOf === plan.variantOf ? 'ring-2 ring-primary' : 'ring-1 ring-gray-900/10 dark:ring-white/10',
                      'pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 rounded-lg sm:block',
                    )}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <Modal visible={showModal} onClose={toggleModal}>
        <h2 className="text-2xl dark:text-white mb-5">Contact us</h2>
        <div className="flex items-center gap-x-3 text-primary">
          <p className="text-slate-800 dark:text-darkText">Email:</p>
          <a href="mailto:admin@das-ar.com" className="font-semibold underline sm:no-underline hover:underline">
            admin@das-ar.com
          </a>
          <ButtonClipboard text="admin@das-ar.com" />
        </div>
      </Modal>
    </section>
  );
};

export default React.memo(PlansTableMobile);
